import { useState } from "react";
import useReminders from "../../context/useReminders.js";
import useAuth from "../../context/useAuth.js";
import settingsApi from "../../services/settings.api.js";
import useApi, { useMutation } from "../../hooks/useApi.js";
import Button from "../ui/Button.jsx";
import Modal from "../ui/Modal.jsx";
import { Select } from "../ui/Form.jsx";
import { Alert, Notice } from "../ui/Feedback.jsx";
import { isAdmin } from "../../utils/permissions.js";

/** Human copy for every `Notification.permission` value we can observe. */
const PERMISSION_COPY = {
  granted: {
    label: "Notifications enabled",
    hint: "This device will alert you before each appointment.",
    tone: "text-tertiary",
    icon: "notifications_active",
  },
  default: {
    label: "Notifications off",
    hint: "Turn on alerts to be reminded before appointments.",
    tone: "text-on-surface-variant",
    icon: "notifications",
  },
  denied: {
    label: "Notifications blocked",
    hint: "Native pop-ups are off. The in-app banner and chime still alert you — allow notifications for this site in your browser settings to add pop-ups.",
    tone: "text-error",
    icon: "notifications_off",
  },
  unsupported: {
    label: "Notifications unavailable",
    hint: "This browser has no web-notification support. The in-app banner and chime still alert you.",
    tone: "text-on-surface-variant",
    icon: "notifications_off",
  },
};

const copyFor = (permission) =>
  PERMISSION_COPY[permission] ?? PERMISSION_COPY.default;

/**
 * Top-bar bell — a real control, not a decoration. It opens a small panel that
 * requests browser permission on an explicit click (a page-load prompt would be
 * ignored or auto-denied by modern browsers) and offers a one-tap test.
 */
export function ReminderNotificationButton() {
  const reminders = useReminders();
  const [open, setOpen] = useState(false);
  const permission = reminders?.permission ?? "unsupported";
  const copy = copyFor(permission);
  const granted = permission === "granted";

  if (!reminders) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="relative flex h-12 w-12 items-center justify-center rounded-full text-on-surface-variant transition-all duration-200 hover:bg-surface-container active:scale-90 lg:h-10 lg:w-10"
        title={copy.label}
        aria-label={`Appointment notifications: ${copy.label}`}
      >
        <span
          className={`material-symbols-outlined ${granted ? "material-filled text-primary" : ""}`}
          aria-hidden="true"
        >
          {copy.icon}
        </span>
        {!granted && permission !== "unsupported" && (
          <span className="absolute right-3 top-3 h-2 w-2 rounded-full border-2 border-surface bg-primary lg:right-2 lg:top-2" />
        )}
      </button>

      <NotificationPanel open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/** Modal body shared by the top-bar bell and the appointments settings panel. */
function NotificationPanel({ open, onClose }) {
  const reminders = useReminders();
  const permission = reminders?.permission ?? "unsupported";
  const copy = copyFor(permission);
  const [note, setNote] = useState(null);

  // Clear the transient note on close so reopening always starts fresh.
  const close = () => {
    setNote(null);
    onClose?.();
  };

  const enable = async () => {
    const result = await reminders?.requestPermission();
    if (result === "granted") setNote("Reminders are on for this device.");
    else if (result === "denied")
      setNote("Permission denied. Allow notifications in your browser settings.");
    else setNote(null);
  };

  const test = async () => {
    const result = await reminders?.sendTestNotification();
    if (result === "granted")
      setNote("Test alert sent — check the glowing banner at the top of the screen.");
    else if (result === "denied")
      setNote(
        "Browser pop-ups are blocked, but the in-app banner and chime were still shown.",
      );
    else if (result === "unsupported")
      setNote(
        "This browser can't show web notifications — the in-app banner and chime were still shown.",
      );
    else setNote(null);
  };

  return (
    <Modal open={open} onClose={close} title="Appointment Notifications" size="sm">
      <div className="space-y-5">
        <div className="flex items-start gap-4 rounded-xl bg-surface-container-low p-4">
          <span
            className={`material-symbols-outlined text-2xl ${copy.tone}`}
            aria-hidden="true"
          >
            {copy.icon}
          </span>
          <div>
            <p className="text-sm font-bold text-on-surface">{copy.label}</p>
            <p className="mt-0.5 text-sm text-on-surface-variant">{copy.hint}</p>
          </div>
        </div>

        {note && <Notice message={note} onDismiss={() => setNote(null)} />}

        <p className="text-xs leading-relaxed text-on-surface-variant/80">
          Reminders are saved on the server and delivered while the dashboard or
          the installed app is open — no paid background worker required. The
          in-app banner and chime always fire; a native pop-up is added when the
          browser allows it.
        </p>

        <div className="flex flex-wrap justify-end gap-3">
          {permission === "default" && (
            <Button icon="notifications_active" onClick={enable}>
              🔔 Enable Appointment Notifications
            </Button>
          )}
          {/* Offered whatever the native permission is: the in-app banner and
              chime do not need it, so staff can always verify the alert works. */}
          {permission !== "default" && (
            <Button
              variant={permission === "granted" ? "secondary" : "ghost"}
              icon="send"
              onClick={test}
            >
              {permission === "granted" ? "Test Notification" : "Test In-App Alert"}
            </Button>
          )}
          {permission === "unsupported" && (
            <Button variant="ghost" onClick={close}>
              Close
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

/**
 * Admin settings block — default reminder offset plus a live read on the
 * browser permission. Lives on the appointments page so the schedule and its
 * reminder configuration sit together.
 */
export function ReminderSettingsPanel() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const reminders = useReminders();

  const settings = useApi(() => settingsApi.getReminder(), []);
  const save = useMutation(settingsApi.updateReminder);

  // `draft` holds only the un-saved selection; the persisted value is the
  // fallback, so a reload never needs a state-syncing effect.
  const savedOffset = settings.data?.offsetMinutes ?? 30;
  const [draft, setDraft] = useState(null);
  const offset = draft ?? savedOffset;
  const [flash, setFlash] = useState(null);

  const permission = reminders?.permission ?? "unsupported";
  const copy = copyFor(permission);

  const handleSave = async () => {
    setFlash(null);
    try {
      await save.execute(offset);
      settings.reload();
      setDraft(null);
      setFlash("Reminder offset updated.");
    } catch {
      /* surfaced through save.error */
    }
  };

  const handleEnable = async () => {
    setFlash(null);
    const result = await reminders?.requestPermission();
    if (result === "granted") setFlash("Notifications enabled for this device.");
  };

  const handleTest = async () => {
    setFlash(null);
    const result = await reminders?.sendTestNotification();
    if (result === "granted") {
      setFlash("Test alert sent — banner and chime shown at the top of the screen.");
    } else {
      setFlash(
        "Native pop-ups are unavailable, but the in-app banner and chime were still shown.",
      );
    }
  };

  return (
    <div className="card-surface-gold mb-8 overflow-hidden">
      <div className="flex flex-col gap-6 bg-surface-container-low p-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary-container/10 text-primary-container">
            <span className="material-symbols-outlined text-2xl" aria-hidden="true">
              alarm
            </span>
          </div>
          <div>
            <h3 className="font-headline text-lg font-bold text-on-surface">
              Appointment Reminders
            </h3>
            <p className="mt-0.5 text-sm text-on-surface-variant">
              In-app banner and chime before each booking — plus a native browser
              pop-up whenever the browser allows it.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 rounded-xl bg-surface-container-lowest px-4 py-2.5">
            <span className={`material-symbols-outlined text-lg ${copy.tone}`} aria-hidden="true">
              {copy.icon}
            </span>
            <span className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">
              {copy.label}
            </span>
          </div>

          {admin && permission !== "granted" && permission !== "unsupported" && (
            <Button size="sm" icon="notifications_active" onClick={handleEnable}>
              Enable
            </Button>
          )}
          {/* Always testable: the banner + chime work without native permission. */}
          {permission !== "default" && (
            <Button
              size="sm"
              variant={permission === "granted" ? "secondary" : "ghost"}
              icon="send"
              onClick={handleTest}
            >
              Test
            </Button>
          )}
        </div>
      </div>

      <div className="p-6">
        {flash && (
          <Notice
            className="mb-4"
            message={flash}
            onDismiss={() => setFlash(null)}
          />
        )}
        <Alert error={settings.error} onRetry={settings.reload} className="mb-4" />
        <Alert error={save.error} className="mb-4" />

        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="w-full sm:max-w-xs">
            <label
              htmlFor="reminder-offset"
              className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-on-surface-variant"
            >
              Default reminder lead time
            </label>
            <Select
              id="reminder-offset"
              value={offset}
              disabled={!admin || save.pending}
              onChange={(event) => setDraft(Number(event.target.value))}
            >
              <option value={15}>15 minutes before</option>
              <option value={30}>30 minutes before</option>
              <option value={60}>1 hour before</option>
            </Select>
            <p className="mt-1.5 text-xs text-on-surface-variant/70">
              New and rescheduled appointments use this.{" "}
              {admin ? "" : "Ask an admin to change it."}
            </p>
          </div>

          {admin && (
            <Button
              icon="save"
              onClick={handleSave}
              loading={save.pending}
              disabled={settings.loading || offset === savedOffset}
            >
              Save offset
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReminderNotificationButton;
